import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    RootState,
    useAppSelector,
    useAppDispatch,
    getTriageChanges,
} from '../../redux';
import { useTranslation } from 'react-i18next';
import { Change } from '../../models';
import { ChangeCard, ChangeModal, Icon, WidgetSkeleton } from '../../components';
import { useDashboardChangeModal } from '../../utils/useDashboardChangeModal';

const DISPLAY_LIMIT = 8;
const PENDING_TRIAGE_LINK = '/changes?status=pendingTriage';

function TriageChanges() {
    const { t }    = useTranslation();
    const dispatch = useAppDispatch();
    const { triageChanges, triageChangesStatus } = useAppSelector((state: RootState) => state.changes);
    const {
        modalChangeId,
        detailChange,
        openChangeModal,
        closeChangeModal,
    } = useDashboardChangeModal();

    const isLoading = (triageChangesStatus === 'pending' || triageChangesStatus === 'idle')
        && !triageChanges?.length;

    useEffect(() => {
        dispatch(getTriageChanges({}));
    }, [dispatch]);

    const { sentryBugs, otherRequests } = useMemo(() => ({
        sentryBugs: triageChanges.filter((item: Change) => item.issue?.source === 'sentry'),
        otherRequests: triageChanges.filter((item: Change) => item.issue?.source !== 'sentry'),
    }), [triageChanges]);

    const reloadTriage = () => {
        dispatch(getTriageChanges({}));
    };

    const onRemoveCard = (_removed: AnyObj) => {
        // Soft-delete is handled inside ChangeModal; refresh the list.
        closeChangeModal();
        reloadTriage();
    };

    if (isLoading) {
        return <WidgetSkeleton rows={4} />;
    }

    if (!triageChanges?.length) {
        return null;
    }

    const renderSection = (items: Change[], title: string, truncate = false) => {
        const sorted = items.sort((a: Change, b: Change) => b.slug - a.slug);
        const visible = sorted.slice(0, DISPLAY_LIMIT);
        const hiddenCount = sorted.length - visible.length;

        return (
            <div className="mb-3">
                <div className="mb-1 text-xs font-semibold uppercase text-gray-600 dark:text-gray-300">
                    {title} ({items.length})
                </div>
                <div className="space-y-1.5">
                    {visible.map((change: Change) => (
                        <ChangeCard
                            key={change._id}
                            change={change}
                            showProject
                            truncateTitle={truncate ? 72 : undefined}
                            className={truncate ? 'bg-red-50 dark:bg-red-950/30/50 dark:bg-red-950/20' : 'bg-orange-50 dark:bg-orange-950/30/50 dark:bg-orange-950/20'}
                            onClick={() => openChangeModal(change)}
                        />
                    ))}
                </div>
                {hiddenCount > 0 && (
                    <Link to={PENDING_TRIAGE_LINK} className="mt-1 inline-block text-xs text-indigo-600 hover:underline">
                        {t('dashboard.show_more', { count: hiddenCount })}
                    </Link>
                )}
            </div>
        );
    };

    return (
        <div className="TriageChanges rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-end justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <h3 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white">
                    <Icon type="task" size={6} className="mr-1.5" />
                    {t('changes.triage_submissions')}
                    <span className="ml-1 text-base font-normal text-gray-500 dark:text-gray-400">({triageChanges.length})</span>
                </h3>
                <Link to={PENDING_TRIAGE_LINK} className="text-xs text-indigo-600 hover:underline">
                    {t('common.see_all')}
                    <Icon type="right" className="inline" />
                </Link>
            </div>
            <div className="max-h-[450px] overflow-y-auto p-4">
                {sentryBugs.length > 0 && renderSection(sentryBugs, t('changes.bug_submissions'), true)}
                {otherRequests.length > 0 && renderSection(otherRequests, t('changes.other_submissions'))}
            </div>

            {detailChange && detailChange._id === modalChangeId && detailChange.project?.name && (
                <ChangeModal
                    project={detailChange.project}
                    change={detailChange}
                    show={true}
                    showProjectName={true}
                    onClose={closeChangeModal}
                    onRemove={onRemoveCard}
                />
            )}
        </div>
    );
}

export default TriageChanges;
